import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { clearCachedOrg } from '@/contexts/OrganizationContext';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up listener first to not miss events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (event === 'SIGNED_OUT') {
          clearCachedOrg();
        }
      }
    );

    // Then restore session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    // Routed through the `signup` edge function which enforces
    // disposable-domain blocklist + per-IP rate limiting before
    // creating the auth user. Email verification is still required
    // before the user can create their organization.
    const redirectUrl = `${window.location.origin}/`;
    const { data, error: invokeError } = await supabase.functions.invoke('signup', {
      body: { email, password, redirectTo: redirectUrl },
    });
    if (invokeError) {
      // Edge function returned non-2xx — surface its message if present.
      const msg = (data as { error?: string } | null)?.error || invokeError.message;
      return { error: new Error(msg) };
    }
    if (data && typeof data === 'object' && 'error' in data && data.error) {
      return { error: new Error(String(data.error)) };
    }
    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    clearCachedOrg();
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
